import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/PlanUpgrade.css';
import { COLORS } from '../styles/colors';
// 로고 이미지 import
import logoImage from '../assets/tapgo-logo.png'; // 로고 변경
import { loadTossPayments } from '@tosspayments/payment-sdk';

const PlanUpgrade = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tossPayments, setTossPayments] = useState(null);
  
  // 각 회사 규모별로 독립적인 상태 관리
  const [activePlanDurations, setActivePlanDurations] = useState({
    '소규모': '1개월 플랜',
    '중간 규모': '1개월 플랜',
    '대형': '1개월 플랜'
  });

  // 회사 규모 및 기간별 가격 정보
  const planPrices = {
    '소규모': {
      '1개월 플랜': 1,
      '6개월 플랜': 390000,
      '12개월 플랜': 588000,
    },
    '중간 규모': {
      '1개월 플랜': 129000,
      '6개월 플랜': 690000,
      '12개월 플랜': 1188000,
    },
    '대형': {
      '1개월 플랜': 199000,
      '6개월 플랜': 1074000,
      '12개월 플랜': 1788000,
    }
  };

  // 플랜 기간 목록
  const planDurations = ['1개월 플랜', '6개월 플랜', '12개월 플랜'];

  // 회사 규모별 설명
  const sizeDescriptions = {
    '소규모': '10인 이하',
    '중간 규모': '11 - 50인',
    '대형': '51 - 100인'
  };

  // 할인 정보 계산
  const getDiscountInfo = (companySize, planDuration) => {
    if (planDuration === '1개월 플랜') {
      return { amount: 0, percentage: 0 };
    } else if (planDuration === '6개월 플랜') {
      // 6개월 플랜은 1개월 × 6의 가격에서 약 10% 할인된 가격
      const monthlyPrice = planPrices[companySize]['1개월 플랜'];
      const fullPrice = monthlyPrice * 6;
      const discountAmount = fullPrice - planPrices[companySize][planDuration];
      return { amount: discountAmount, percentage: 10 };
    } else if (planDuration === '12개월 플랜') {
      // 12개월 플랜은 1개월 × 12의 가격에서 약 25% 할인된 가격
      const monthlyPrice = planPrices[companySize]['1개월 플랜'];
      const fullPrice = monthlyPrice * 12;
      const discountAmount = fullPrice - planPrices[companySize][planDuration];
      return { amount: discountAmount, percentage: 25 };
    }
  };

  // 월별 가격 계산
  const getMonthlyPrice = (companySize, planDuration) => {
    const totalPrice = planPrices[companySize][planDuration];
    const months = planDuration === '1개월 플랜' ? 1 : planDuration === '6개월 플랜' ? 6 : 12;
    return Math.floor(totalPrice / months);
  };

  // 특정 회사 규모의 다음 플랜으로 이동
  const nextPlan = (companySize) => {
    const currentPlan = activePlanDurations[companySize];
    const currentIndex = planDurations.indexOf(currentPlan);
    const nextIndex = (currentIndex + 1) % planDurations.length;
    
    setActivePlanDurations(prev => ({
      ...prev,
      [companySize]: planDurations[nextIndex]
    }));
  };

  // 특정 회사 규모의 이전 플랜으로 이동
  const prevPlan = (companySize) => {
    const currentPlan = activePlanDurations[companySize];
    const currentIndex = planDurations.indexOf(currentPlan);
    const prevIndex = (currentIndex - 1 + planDurations.length) % planDurations.length;
    
    setActivePlanDurations(prev => ({
      ...prev,
      [companySize]: planDurations[prevIndex]
    }));
  };

  // 토스페이먼츠 SDK 초기화
  useEffect(() => {
    // 테스트 키로 변경 (실제 환경에서는 다시 라이브 키로 변경 필요)
    const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY;
    
    console.log('토스페이먼츠 SDK 초기화 시작, 클라이언트 키:', clientKey);
    
    loadTossPayments(clientKey).then(tossPayments => {
      console.log('토스페이먼츠 SDK 초기화 성공');
      setTossPayments(tossPayments);
    }).catch(error => {
      console.error('토스페이먼츠 SDK 초기화 실패:', error);
      setError('결제 모듈 초기화에 실패했습니다.');
    });
  }, []);

  // 플랜 유형 매핑 (UI 플랜 -> API 플랜 타입)
  const planTypeMapping = {
    '1개월 플랜': 'basic',
    '6개월 플랜': 'premium',
    '12개월 플랜': 'enterprise'
  };

  const handleSubscribe = async (companySize, planDuration) => {
    setError(null);
    setIsLoading(true);
    
    try {
      // API 호출에 사용할 플랜 타입 변환
      const apiPlanType = planTypeMapping[planDuration];
      
      console.log(`${companySize} ${planDuration} 구독 신청 (${apiPlanType}, 카드결제)`);
      
      if (!tossPayments) {
        throw new Error('결제 모듈이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
      }
      
      // 주문 ID 생성 (영문, 숫자, -, _만 포함하고 6-64자 이내로 제한)
      const orderId = `tapgo-${Date.now()}-${Math.floor(Math.random() * 1000)}-${apiPlanType}`;
      
      // 결제 파라미터 로깅
      const paymentParams = {
        amount: planPrices[companySize][planDuration],
        orderId: orderId,
        orderName: `TapGo Plus ${companySize} ${planDuration} 구독`,
        customerName: '고객',
        successUrl: `${window.location.origin}/payment-success`,
        failUrl: `${window.location.origin}/plan-upgrade`
      };
      
      console.log('결제 요청 파라미터:', JSON.stringify(paymentParams));
      console.log('현재 도메인:', window.location.origin);
      
      try {
        // 토스페이먼츠 결제창 열기
        await tossPayments.requestPayment('카드', paymentParams);
      } catch (paymentError) {
        console.error('토스페이먼츠 결제창 오류:', paymentError);
        
        // 토스페이먼츠 에러 코드 및 메시지 확인
        if (paymentError.code) {
          console.error('에러 코드:', paymentError.code);
          console.error('에러 메시지:', paymentError.message);
          throw new Error(`결제 오류: ${paymentError.message}`);
        } else {
          throw paymentError;
        }
      }
      
      // 결제창이 열리면 이 아래 코드는 실행되지 않고, 결제 성공 시 successUrl로 리다이렉트됩니다.
      // 결제 실패 시 failUrl로 리다이렉트됩니다.
      
    } catch (error) {
      console.error('결제 실패:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
        <div className="appBarLogo">
          <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
        </div>
      </div>
      
      <div className="planContainer">
        <h1 className="planTitle">플랜 업그레이드</h1>
        <p className="planSubtitle">유료 플랜으로만 서비스를 이용할 수 있어요</p>
        
        {error && <div className="errorMessage">{error}</div>}
        
        <div className="planCards">
          {/* 소규모 */}
          <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
            <h2 className="planName">소규모</h2>
            <p className="sizeDescription">({sizeDescriptions['소규모']})</p>
            
            {activePlanDurations['소규모'] === '12개월 플랜' && (
              <div className="mostPopular">
                "가장 많이 선택한"
              </div>
            )}
            
            <div className="planCarousel">
              <button className="carouselButton left" onClick={() => prevPlan('소규모')}>
                &#10094;
              </button>
              
              <div className="planDuration">
                <h3>{activePlanDurations['소규모']}</h3>
                <div className="planPrice">
                  <span className="pricePrefix">월</span>
                  <span className="priceAmount">{getMonthlyPrice('소규모', activePlanDurations['소규모']).toLocaleString()}</span>
                  <span className="priceSuffix">원</span>
                </div>
                
                <div className="planDetails">
                  <div className="totalPrice">
                    <span className="detailLabel">총 결제 금액</span>
                    <span className="detailValue">{planPrices['소규모'][activePlanDurations['소규모']].toLocaleString()} 원</span>
                  </div>
                  <div className="discountPrice">
                    <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                    <span className="detailValue" style={{ color: '#6C80E9' }}>
                      {getDiscountInfo('소규모', activePlanDurations['소규모']).amount > 0 
                        ? `${getDiscountInfo('소규모', activePlanDurations['소규모']).amount.toLocaleString()} 원 (${getDiscountInfo('소규모', activePlanDurations['소규모']).percentage}%)` 
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <button className="carouselButton right" onClick={() => nextPlan('소규모')}>
                &#10095;
              </button>
            </div>
            
            <button 
              className="subscribeButton"
              onClick={() => handleSubscribe('소규모', activePlanDurations['소규모'])}
              style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
              disabled={isLoading}
            >
              <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
              <span className="buttonText">
                {isLoading ? '처리 중...' : 'TapGo Plus 구독하기'}
              </span>
            </button>
          </div>
          
          {/* 중간 규모 */}
          <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
            <h2 className="planName">중간 규모</h2>
            <p className="sizeDescription">({sizeDescriptions['중간 규모']})</p>
            
            {activePlanDurations['중간 규모'] === '12개월 플랜' && (
              <div className="mostPopular">
                "가장 많이 선택한"
              </div>
            )}
            
            <div className="planCarousel">
              <button className="carouselButton left" onClick={() => prevPlan('중간 규모')}>
                &#10094;
              </button>
              
              <div className="planDuration">
                <h3>{activePlanDurations['중간 규모']}</h3>
                <div className="planPrice">
                  <span className="pricePrefix">월</span>
                  <span className="priceAmount">{getMonthlyPrice('중간 규모', activePlanDurations['중간 규모']).toLocaleString()}</span>
                  <span className="priceSuffix">원</span>
                </div>
                
                <div className="planDetails">
                  <div className="totalPrice">
                    <span className="detailLabel">총 결제 금액</span>
                    <span className="detailValue">{planPrices['중간 규모'][activePlanDurations['중간 규모']].toLocaleString()} 원</span>
                  </div>
                  <div className="discountPrice">
                    <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                    <span className="detailValue" style={{ color: '#6C80E9' }}>
                      {getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).amount > 0 
                        ? `${getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).amount.toLocaleString()} 원 (${getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).percentage}%)` 
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <button className="carouselButton right" onClick={() => nextPlan('중간 규모')}>
                &#10095;
              </button>
            </div>
            
            <button 
              className="subscribeButton"
              onClick={() => handleSubscribe('중간 규모', activePlanDurations['중간 규모'])}
              style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
              disabled={isLoading}
            >
              <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
              <span className="buttonText">
                {isLoading ? '처리 중...' : 'TapGo Plus 구독하기'}
              </span>
            </button>
          </div>
          
          {/* 대형 */}
          <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
            <h2 className="planName">대형 규모</h2>
            <p className="sizeDescription">({sizeDescriptions['대형']})</p>
            
            {activePlanDurations['대형'] === '12개월 플랜' && (
              <div className="mostPopular">
                "가장 많이 선택한"
              </div>
            )}
            
            <div className="planCarousel">
              <button className="carouselButton left" onClick={() => prevPlan('대형')}>
                &#10094;
              </button>
              
              <div className="planDuration">
                <h3>{activePlanDurations['대형']}</h3>
                <div className="planPrice">
                  <span className="pricePrefix">월</span>
                  <span className="priceAmount">{getMonthlyPrice('대형', activePlanDurations['대형']).toLocaleString()}</span>
                  <span className="priceSuffix">원</span>
                </div>
                
                <div className="planDetails">
                  <div className="totalPrice">
                    <span className="detailLabel">총 결제 금액</span>
                    <span className="detailValue">{planPrices['대형'][activePlanDurations['대형']].toLocaleString()} 원</span>
                  </div>
                  <div className="discountPrice">
                    <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                    <span className="detailValue" style={{ color: '#6C80E9' }}>
                      {getDiscountInfo('대형', activePlanDurations['대형']).amount > 0 
                        ? `${getDiscountInfo('대형', activePlanDurations['대형']).amount.toLocaleString()} 원 (${getDiscountInfo('대형', activePlanDurations['대형']).percentage}%)` 
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <button className="carouselButton right" onClick={() => nextPlan('대형')}>
                &#10095;
              </button>
            </div>
            
            <button 
              className="subscribeButton"
              onClick={() => handleSubscribe('대형', activePlanDurations['대형'])}
              style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
              disabled={isLoading}
            >
              <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
              <span className="buttonText">
                {isLoading ? '처리 중...' : 'TapGo Plus 구독하기'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlanUpgrade; 